import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Blocker, useBlocker } from 'react-router';

type NavigationBlockerContextInternal = {
    blocker: Blocker;
    setShouldBlockNavigation: (active: boolean) => void;
};

export const NavigationBlockerContext = createContext<NavigationBlockerContextInternal | null>(null);

export interface INavigationBlockerProviderProps {
    children: React.ReactNode;
}

/**
 * Single shared {@link useBlocker} for descendants. Mirrors {@link usePreventNavigation} in
 * `hooks/usePreventNavigation.ts`: SPA transitions (pathname change) and `beforeunload`.
 */
export const NavigationBlockerProvider: React.FC<INavigationBlockerProviderProps> = ({ children }) => {
    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => shouldBlockNavigation && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        const browserNativePreventNavigation = (event: BeforeUnloadEvent) => {
            if (shouldBlockNavigation) {
                event.preventDefault();
            }
        };
        window.addEventListener('beforeunload', browserNativePreventNavigation);
        return () => window.removeEventListener('beforeunload', browserNativePreventNavigation);
    }, [shouldBlockNavigation]);

    const value = useMemo(
        () => ({
            blocker,
            setShouldBlockNavigation,
        }),
        [blocker, setShouldBlockNavigation]
    );

    return <NavigationBlockerContext.Provider value={value}>{children}</NavigationBlockerContext.Provider>;
};
